<!-- eslint-disable vue/no-v-html -->
<template>
  <div>
    <div class="min-w-0 flex-1">
      <div class="flex flex-wrap items-center gap-2">
        <div v-if="isUnseen" class="bg-danger size-2 shrink-0 rounded-full" />
        <BaseBadge :class="badge.class">
          <BaseIcon :name="badge.icon" class="mr-1" />
          {{ badge.label }}
        </BaseBadge>
        <span class="font-medium text-gray-900">
          {{ notification.title }}
        </span>
        <span v-if="time" class="text-sm text-gray-300">{{ time }}</span>
      </div>
      <div class="notification mt-1 text-sm" v-html="renderedDescription" />
      <template v-if="notification.content">
        <div
          v-if="showContent"
          class="notification mt-2 text-sm"
          v-html="renderedContent"
        />
        <button
          class="text-primary-600 mt-1 text-sm hover:underline"
          @click="showContent = !showContent"
        >
          {{ showContent ? "Toon minder informatie" : "Toon meer informatie" }}
        </button>
      </template>
    </div>
  </div>
</template>

<script setup>
import { computed, ref } from "vue"
import { format } from "date-fns"
import { marked } from "marked"
import DOMPurify from "dompurify"
import { BaseBadge, BaseIcon } from "@repowerednl/ui-component-library"
import useUserStore from "@/stores/userStore.js"

const props = defineProps({
  notification: {
    type: Object,
    required: true,
  },
})

const userStore = useUserStore()

const showContent = ref(false)

const isUnseen = computed(() => {
  if (!userStore.notificationsSeenUntil) {
    return false
  }
  return (
    new Date(props.notification.timestamp) >
    new Date(userStore.notificationsSeenUntil)
  )
})

const render = (text) => DOMPurify.sanitize(marked(text))

const renderedDescription = computed(() =>
  props.notification.description ? render(props.notification.description) : "",
)

const renderedContent = computed(() =>
  props.notification.content ? render(props.notification.content) : "",
)

const time = computed(() => {
  const date = new Date(props.notification.timestamp)
  return Number.isNaN(date.getTime()) ? null : format(date, "HH:mm")
})

const BADGE_CONFIG = {
  GENERAL: {
    label: "Notificatie",
    class: "!bg-gray-100 !text-gray-900",
    icon: "notification",
  },
  RELEASE_NOTES: {
    label: "Release notes",
    class: "!bg-finance-100 !text-finance-900",
    icon: "barricade",
  },
}

const DEFAULT_BADGE = {
  label: "Notificatie",
  class: "!bg-gray-100 !text-gray-900",
  icon: "information",
}

const badge = computed(
  () => BADGE_CONFIG[props.notification.channel] ?? DEFAULT_BADGE,
)
</script>

<!-- Global style needed because v-html content cannot be targeted with scoped styles -->
<style>
.notification p {
  margin-top: 0.25rem;
  margin-bottom: 0.25rem;
  max-width: 65ch;
}

.notification ul {
  margin-top: 0.25rem;
  margin-bottom: 0.25rem;
  list-style-type: disc;
  padding-left: 1rem;
}

.notification ul::marker {
  color: var(--color-primary-500);
}

.notification ol {
  margin-top: 0.25rem;
  margin-bottom: 0.25rem;
  list-style-type: decimal;
  padding-left: 1rem;
}

.notification ol::marker {
  color: var(--color-primary-500);
}

.notification a {
  text-decoration: underline;
  text-decoration-color: var(--color-primary-500);
  text-decoration-thickness: 2px;
}

.notification a:hover {
  color: var(--color-primary-500);
}

.notification img {
  margin-top: 1rem;
  margin-bottom: 1rem;
  width: 100%;
  border-radius: 1rem;
  border: 1px solid var(--color-gray-100);
  box-shadow:
    0 1px 3px 0 rgb(0 0 0 / 0.1),
    0 1px 2px -1px rgb(0 0 0 / 0.1);
}

.notification h1 {
  margin-bottom: 0.5rem;
  margin-top: 1.5rem;
  border-bottom: 1px solid var(--color-gray-200);
  padding-bottom: 0.5rem;
  font-size: 1.5rem;
  line-height: 2rem;
  font-weight: 400;
}

.notification h2 {
  margin-bottom: 0.25rem;
  margin-top: 1rem;
  font-size: 1.25rem;
  line-height: 1.75rem;
  font-weight: 500;
}

.notification h3 {
  margin-bottom: 0.25rem;
  margin-top: 0.5rem;
  font-size: 1.125rem;
  line-height: 1.75rem;
  font-weight: 700;
}
</style>
